import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from './Modal';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  details?: Record<string, any> | string | null;
  warningMessage?: string | null;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'danger' | 'secondary' | 'accent' | 'warning' | 'info';
  isLoading?: boolean;
  /** Use when dialog must appear above other overlays (e.g. above a presets panel at z-[100]) */
  zIndex?: number;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  details = null,
  warningMessage = null,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'danger',
  isLoading = false,
  zIndex,
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} zIndex={zIndex}>
      <ModalHeader>
        <ModalTitle>
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'inline-flex h-8 w-8 shrink-0 items-center justify-center',
                isDark ? 'text-amber-300' : 'text-amber-700',
              )}
              aria-hidden="true"
            >
              <svg className="h-4.5 w-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a1 1 0 00.86 1.5h18.64a1 1 0 00.86-1.5L13.71 3.86a1 1 0 00-1.72 0z" />
              </svg>
            </span>
            <span className="text-left">{title}</span>
          </div>
        </ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4 text-left">
          <p className={cn('text-sm leading-relaxed', colors.textSecondary)}>
            {message}
          </p>

          {details && (
            <div className={cn('rounded-lg border p-3.5', colors.surfaceHover, colors.border)}>
              <p className={cn('mb-2 text-[11px] font-semibold uppercase tracking-wide', colors.textSecondary)}>
                Details
              </p>
              <div className={cn('space-y-1 text-xs leading-relaxed', colors.textSecondary)}>
                {typeof details === 'object' ? (
                  Object.entries(details).map(([key, value]) => (
                    <div key={key} className="max-w-full break-words [overflow-wrap:anywhere]">
                      <strong>{key}:</strong> {String(value)}
                    </div>
                  ))
                ) : (
                  <div className="max-w-full break-words [overflow-wrap:anywhere]">{details}</div>
                )}
              </div>
            </div>
          )}

          {warningMessage && (
            <div
              className={cn(
                'rounded-lg border px-3.5 py-3',
                colors.error,
                isDark ? 'border-red-500/40' : 'border-red-200',
              )}
              role="alert"
              aria-live="polite"
            >
              <div className="flex items-start gap-2.5">
                <span
                  className={cn('mt-0.5 shrink-0', isDark ? 'text-red-300' : 'text-red-600')}
                  aria-hidden="true"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a1 1 0 00.86 1.5h18.64a1 1 0 00.86-1.5L13.71 3.86a1 1 0 00-1.72 0z"
                    />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className={cn('text-xs font-semibold uppercase tracking-wide')}>Warning</p>
                  <p className="mt-1 text-sm leading-relaxed">{warningMessage}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex w-full items-center justify-end gap-2.5">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            variant={confirmVariant}
            disabled={isLoading}
            loading={isLoading}
          >
            {confirmText}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmationDialog;

